import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import LogoIcon from 'components/App/Header/Logo/LogoIcon'
import TextButton from 'LuxKit/Button/TextButton'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import Flag from 'LuxKit/Flag'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LineUserCircleIcon from 'LuxKit/Icons/line/LineUserCircleIcon'
import Modal from 'LuxKit/Modal/Modal'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import NavBarListItem from 'components/NavKit/NavBarListItem'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Expander from 'components/utils/Expander'
import GeoContext from 'contexts/geoContext'
import ModalContext from 'contexts/ModalContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppSelector } from 'hooks/reduxHooks'
import useToggle from 'hooks/useToggle'
import { partitionBy, skip, take } from 'lib/array/arrayUtils'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import React, { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { getFlatNavItems } from 'selectors/navigationSelectors'
import useLocalizedNavItems from 'hooks/useLocalizedNavItems'
import NavBarContactsItems from '../NavBarButtons/NavBarContactsItems'
import NavBarRegionItems from '../NavBarButtons/NavBarRegionItems'
import NavBarHamburgerMenuItem from './NavBarHamburgerMenuItem'
import NavBarHamburgerMenuLoyaltyItem from './NavBarHamburgerMenuLoyaltyItem'
import NavBarListItemDivider from '../NavBarListItemDivider'
import Clickable from 'components/Common/Clickable'
import config from 'constants/config'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import NavBarHamburgerMenuLoyaltyFooter from './NavBarHamburgerMenuLoyaltyFooter'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import NavBarHamburgerMenuModalContent from './NavBarHambugerMenuModalContent'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'

function NavBarHamburgerMenu() {
  const intl = useIntl()
  const [showMore, toggleMore] = useToggle()
  const { currentRegionCode, currentCurrency } = useContext(GeoContext)
  const { resolve } = useModalElementContext()
  const showModal = useContext(ModalContext)
  const loggedIn = useAppSelector(selectLoggedIn)
  const rawNavItems = useAppSelector(state => getFlatNavItems(state))
  const navItems = useLocalizedNavItems(rawNavItems)
  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const loyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const isCurrentRegionUserLoyaltyRegion = useAppSelector(getIsCurrentRegionUserLoyaltyRegion)
  const onMoreClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    toggleMore()
    if (!showMore) {
      // toggling it on, scroll it into view
      e.currentTarget.scrollIntoView({ block: 'start', behavior: 'smooth' })
    }
  }, [showMore, toggleMore])

  const onRegionClick = useCallback(() => {
    showModal(resolve => <Modal
      size="S"
      title={intl.formatMessage({
        id: 'jZ_em4vQCeUJW4EVMsbw',
        defaultMessage: 'Select your region & currency',
        description: 'Modal title for region and currency selection in the navigation bar',
      })}
    >
      <NavBarRegionItems onRegionSelect={resolve} />
    </Modal>)
  }, [showModal, intl])
  const onLogin = useCallback(() => showModal(<AccountAccessModal />), [showModal])

  const closeModal = useCallback(() => resolve(), [resolve])
  const onNavItemClick = useCallback((_navItem: App.NavItem) => {
    closeModal()
  }, [closeModal])

  const hasSomeLoyalty = luxPlusEnabled || loyaltyEnabled

  const [primaryItems, secondaryItems, moreItems] = useMemo(() => {
    const [primaryNavs, otherItems] = partitionBy(navItems, nav => nav.category === 'primary')
    if (navItems.length <= 11) {
      // We show 10 items and put anything else in more, but there's no point
      // in showing a 'more' with one item, hence we break on 11 here, not 10
      return [primaryNavs, otherItems, []]
    }

    const remainingItems = 10 - primaryNavs.length
    return [
      primaryNavs,
      take(otherItems, remainingItems),
      skip(otherItems, remainingItems),
    ]
  }, [navItems])

  return <ModalBase mode="menu">
    <ModalHeader title={<Clickable onClick={closeModal} to={`/${config.defaultHomePage}`}>
      <LogoIcon height="1em" width={null} />
    </Clickable>}
    />
    <ModalBody>
      <NavBarHamburgerMenuModalContent disablePadding={!isCurrentRegionUserLoyaltyRegion}>
        <VerticalSpacer gap={8}>
          {primaryItems.map(item => <NavBarHamburgerMenuItem
            key={item.text}
            navItem={item}
            onClick={() => onNavItemClick(item)}
          />)}
          {!!secondaryItems.length && <>
            <NavBarListItemDivider />
            {secondaryItems.map(item => <NavBarHamburgerMenuItem
              key={item.text}
              navItem={item}
              onClick={() => onNavItemClick(item)}
            />)}
          </>}
          {!!moreItems.length && <>
            <NavBarListItem
              onClick={onMoreClick}
              title={intl.formatMessage({
                id: '64nBLX-u5v1ra5Es_1jw',
                defaultMessage: 'More',
                description: 'Action button label to show more navigation items in the navigation bar',
              })}
              endIcon={<DropdownChevron open={showMore} size="S" />}
            />
            <Expander expanded={showMore}>
              {moreItems.map(item => <NavBarHamburgerMenuItem
                key={item.text}
                navItem={item}
                onClick={() => onNavItemClick(item)}
                hideIcon
                indent
              />)}
            </Expander>
          </>}
          <NavBarListItemDivider />
          {hasSomeLoyalty && <>
            {luxPlusEnabled && <NavBarHamburgerMenuLoyaltyItem
              variant="luxPlus"
              onClick={closeModal}
            />}
            {loyaltyEnabled && <NavBarHamburgerMenuLoyaltyItem
              variant="loyalty"
              onClick={closeModal}
              isNew
            />}
            <NavBarListItemDivider />
          </>}
          <NavBarContactsItems />
          <NavBarListItemDivider />
          <NavBarListItem
            title={currentCurrency}
            onClick={onRegionClick}
            startIcon={<Flag countryCode={currentRegionCode} size="S" />}
            endIcon={<CSSBreakpoint max="mobile" as={LineAngleRightIcon} />}
          />
        </VerticalSpacer>
        {!isCurrentRegionUserLoyaltyRegion && hasLoyaltyAccount && <NavBarHamburgerMenuLoyaltyFooter />}
      </NavBarHamburgerMenuModalContent>
    </ModalBody>
    {!loggedIn && <ModalFooter>
      <TextButton
        kind="secondary"
        onClick={onLogin}
        size="large"
        fit="full-width"
        startIcon={<LineUserCircleIcon />}
      >
        {intl.formatMessage({
          id: 'DVut0himStXU8yyJTSJu',
          defaultMessage: 'Sign up or Login',
          description: 'Action button label for sign up or login action in the navigation bar',
        })}
      </TextButton>
    </ModalFooter>}
  </ModalBase>
}

export default NavBarHamburgerMenu
